import { FC, useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigation } from '@/store/navigation';
import { useSettings } from '@/store/settings';
import { useChat } from '@/store/chat';
import { chatApi } from '@/api/chat';
import { notesApi } from '@/api/notes';
import type { Note } from '@/api/notes';
import { useBrain } from '@/hooks/useBrain';
import { useDraggableFab } from '@/hooks/useDraggableFab';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { apiClient } from '@/api/client';
import MarkdownText, { CopyButton } from '@/components/common/MarkdownText';
import {
  listSpeechModels, startSpeechModelDownload,
  transcribeAudio, SpeechModel,
} from '@/api/speech';
import { consumeChatStream } from '@/utils/chatStream';
import { WavRecorder } from '@/utils/wavRecorder';
import { useLLMBalance } from '@/hooks/useLLMBalance';
import { BrainSide } from '@/types';
import { LLM_MODEL_MAP, getBackendModelId, getModelByProviderModel, normalizeOllamaModelName } from '@/config/llmModels';

function isLocalModel(modelId: string): boolean {
  return modelId.startsWith('ollama-') || modelId === 'ollama';
}
import ModelSelector from '@/components/llm/ModelSelector';
import { isPlatformModel, PLATFORM_PREFIX } from '@/components/llm/modelUtils';
import { getModelCatalog } from '@/api/llm';
import { useModelDisplayName } from '@/hooks/useModelDisplayName';

// 桌面端（Electron）判定：桌面端 BYOK 不走平台余额，跳过充值预检
const isDesktop = !!window.psbDesktop?.isDesktop;
import LLMCostBadge from '@/components/llm/LLMCostBadge';
import PaywallModal from '@/components/billing/PaywallModal';
import {
  Brain, Globe,
  Download, Send, ChevronDown, Bot,
  CheckCircle2, XCircle, FileJson, FileText, Trash2, X,
  Home, LogIn, History, MessageSquarePlus, Telescope, Maximize2, Minimize2, RotateCcw
} from 'lucide-react';


interface ChatMessage {
  id: string;
  role: 'user' | 'ai';
  content: string;
  model?: string;
  timestamp: Date;
  isStreaming?: boolean;
  /** 本轮回答零引用源（SSE 未下发非空 sources 事件）→ 显示「去导入」引导 */
  noSources?: boolean;
  /** 规则版 FactCheck 检出的越界引用标记数（>0 时在回答下方显示琥珀色提示） */
  factcheckInvalid?: number;
  /** 智能体轨迹：agent 环逐轮工具调用记录（会话瞬态，不落库不跨刷新） */
  agentTrace?: { round: number; name: string; summary: string; status: string }[];
  agentRounds?: number;
  /** 智能体模式标注（「智能体 · 站内模式」等；仅本轮新消息带，历史回填无） */
  agentLabel?: string;
  /** 写工具待确认卡（流式期间瞬态） */
  pendingConfirm?: { pending_id: string; name: string; summary: string } | null;
}

interface ChatInputBarProps {
  sidebarOpen?: boolean;
  onLoginClick?: () => void;
}

// 智能体开关三态：关 / 站内（知识库+站内工具）/ 站外（另含 web_fetch 联网抓取）
type AgentMode = 'off' | 'internal' | 'external';
// 点击循环切换的顺序：关 → 站内 → 站外 → 关
const AGENT_MODE_CYCLE: Record<AgentMode, AgentMode> = {
  off: 'internal',
  internal: 'external',
  external: 'off',
};

// 气泡上的模型标签：目录名优先，剥 platform: 前缀。
// 必须定义在模块级——在渲染函数内定义会让每次 render 视为新组件类型而重挂子树，
// 流式期间每条 chunk 都触发闪烁
const ModelNameTag: FC<{ model: string }> = ({ model }) => {
  const name = useModelDisplayName(model);
  return <span className="ml-2 opacity-70">· {LLM_MODEL_MAP[model]?.name || name}</span>;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ChatInputBar: FC<ChatInputBarProps> = ({ sidebarOpen = true, onLoginClick }) => {
  const { brainSide } = useNavigation();
  const {
    defaultLLM,
    activeProvider,
    activeModel,
    setActiveProvider,
    ollamaModel,
  } = useSettings();
  const { activeBrain, switchBrain } = useBrain();
  const { isLoggedIn, isLoading: isAuthLoading } = useAuth();
  // 面板开合与活动会话放全局 store：/chat 历史页的「继续对话」可跨页面唤起本组件
  const {
    activeConversationId, setActiveConversationId,
    panelOpen: showChatPanel, setPanelOpen: setShowChatPanel,
  } = useChat();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { balance } = useLLMBalance();

  // 悬浮球可拖动：默认固定 bottom-4 right-6，会压住页面右下角的内容
  // （实捕：落地页 footer 的「扫码」入口被它盖住）。改成可拖动后用户能挪开，
  // 位置存 localStorage；双击复位。位移 <5px 仍按点击处理（否则球点不开面板）。
  const handleFabTap = () => {
    // 未登录沿用原底栏口径：弹登录窗，无回调才兜底跳欢迎页（不裸 navigate）
    if (!isLoggedIn) {
      if (onLoginClick) onLoginClick();
      else navigate('/welcome');
      return;
    }
    setShowChatPanel(true);
  };
  const fab = useDraggableFab(handleFabTap);
  // 目录反查：handleModelChange 写全局 active 时统一为 (provider, provider_model_id)
  // 口径（与右下角控制台一致），避免 catalog id / provider_model_id 两种写法分裂
  const { data: modelCatalog } = useQuery({
    queryKey: ['llmModelCatalog'],
    queryFn: getModelCatalog,
    staleTime: 5 * 60 * 1000,
  });
  const [message, setMessage] = useState('');
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  // 桌面端本地语音：模型选择/下载面板、识别中状态
  const [voiceModels, setVoiceModels] = useState<SpeechModel[]>([]);
  const [showVoicePanel, setShowVoicePanel] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [voiceModelId, setVoiceModelId] = useState(() => {
    try {
      return localStorage.getItem('voice_model_id') || '';
    } catch {
      return '';
    }
  });
  const [attachments, setAttachments] = useState<{ name: string; path: string }[]>([]);
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  // 回形针四功能（09-25 落地）：链接输入弹层 + 笔记挑选弹层的开合与状态
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [showNotePicker, setShowNotePicker] = useState(false);
  const [noteQuery, setNoteQuery] = useState('');

  // 笔记挑选弹窗的搜索（开窗时才发请求，随手型防抖交给 react-query key 切换）
  const { data: notePickerResults } = useQuery({
    queryKey: ['attachment-note-picker', noteQuery],
    queryFn: async () => {
      const response = await notesApi.list({ q: noteQuery || undefined, limit: 8, sort_by: 'updated_at', sort_order: 'desc', include_content: false });
      return response.data;
    },
    enabled: showNotePicker,
    staleTime: 15 * 1000,
  });
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  // 初始模型只做「静态表精确映射」，映射不到（目录行 sys-/opencode- 等）用 defaultLLM。
  // 绝不用 getModelIdByProviderModel——它末尾恒兜底 'ollama'，会把系统模型选择
  // 静默换成本地 0.8b（09-04 生产事故：同会话四个模型混答的根因之一）
  const [currentModel, setCurrentModel] = useState(
    getModelByProviderModel(activeProvider, activeModel)?.id || defaultLLM
  );
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [showBrainSwitcher, setShowBrainSwitcher] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const recognitionRef = useRef<any>(null);
  const wavRecorderRef = useRef<WavRecorder | null>(null);
  const voicePollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // AI 助手面板全屏：默认弹窗（max-w-5xl/70vh），全屏占满视口
  const [chatFullscreen, setChatFullscreen] = useState(false);

  const effectiveBrain = activeBrain || brainSide || 'both';
  const brainLabel = effectiveBrain === 'personal' ? '个人脑' : effectiveBrain === 'network' ? '网络脑' : '双脑融合';
  const BrainIcon = effectiveBrain === 'personal' ? Home : effectiveBrain === 'network' ? Globe : Brain;

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // 面板收起再开也要定位到最后一条：面板关闭时消息子树整棵卸载、scrollTop 归零，
  // 而 messages 没变，上面的滚动 effect 不会重触发——开合状态本身是一路触发源
  useEffect(() => {
    if (showChatPanel) messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
  }, [showChatPanel]);

  // 输入框自适应高度：多行 textarea 随内容长高，清空（发送后）回落；
  // 普通弹窗上限 200px，全屏放宽到 40vh
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const cap = chatFullscreen ? Math.round(window.innerHeight * 0.4) : 200;
    el.style.height = `${Math.min(el.scrollHeight, cap)}px`;
  }, [message, chatFullscreen, showChatPanel]);

  // 会话级模型锁定（09-04 拍板）：会话一旦有消息或有活动会话 id，模型不再被
  // 任何自动逻辑（挂载填充/控制台跟随/设置回读）更换——同一会话混用多个模型是
  // 「忽而博识忽而装傻」的直接根因；用户在选择器里手动改选仍是唯一换模型途径
  const conversationLocked = messages.length > 0 || !!activeConversationId;

  useEffect(() => {
    // 会话锁定期间不回读全局设置——控制台换模型不影响进行中的会话
    if (conversationLocked) return;
    // 只同步本地配置表（LLM_MODEL_MAP）能精确映射的选型；目录行（sys-/opencode- 等）
    // 与 platform: 通道由 ModelSelector 的跟随逻辑处理。
    // 注意必须用 getModelByProviderModel（映射不到返回 undefined）——
    // getModelIdByProviderModel 末尾恒返回 'ollama' 兜底，会把系统模型选择覆写掉
    const exact = getModelByProviderModel(activeProvider, activeModel);
    if (exact) setCurrentModel(exact.id);
  }, [activeProvider, activeModel, conversationLocked]);

  // 对话历史落库：登录用户以服务端会话为准。发送时新建/复用活动会话（见 handleSend），
  // 从 /chat 历史页「继续对话」进入时按 activeConversationId 拉取服务端消息。
  // skipLoadRef：发送中刚创建/复用的会话本地状态最新，跳过一次回拉防覆盖流式气泡。
  const skipConversationLoadRef = useRef<string | null>(null);
  // 请求序号守卫：快速切换会话时旧请求后返回会覆盖新会话消息（串台），
  // 响应落地前比对序号，过期响应（含错误分支）直接丢弃
  const conversationLoadSeqRef = useRef(0);
  // 活动会话形态（chat/agent）：智能体轮次必须落智能体会话（后端 400 硬口径），
  // 普通轮次不挑——混合会话允许，形态以首次落库为准
  const activeConvModeRef = useRef<'chat' | 'agent' | 'unknown'>('unknown');

  useEffect(() => {
    const seq = ++conversationLoadSeqRef.current;
    if (!isLoggedIn || !activeConversationId) return;
    if (skipConversationLoadRef.current === activeConversationId) {
      skipConversationLoadRef.current = null;
      return;
    }
    // 切会话瞬间先把形态复位 unknown（09-27 实捕根修）：fetch 未落地前 ref 残留
    // 上一会话的 'agent' 值，会把「chat 会话 + 智能体开关」漏过后端 400 硬口径
    activeConvModeRef.current = 'unknown';
    chatApi.getConversation(activeConversationId)
      .then((res) => {
        if (seq !== conversationLoadSeqRef.current) return; // 已切走，丢弃过期响应
        activeConvModeRef.current = (res.data.mode as 'chat' | 'agent') || 'chat';
        setMessages(res.data.messages.map((m) => ({
          id: m.id,
          role: m.role === 'user' ? 'user' : 'ai',
          content: m.content,
          model: m.model || undefined,
          timestamp: m.created_at ? new Date(m.created_at) : new Date(),
        })));
        // 会话级模型锁定：打开旧对话时模型跟随该会话最后一轮的模型，
        // 不被控制台/挂载填充顶掉（混模型事故修复的另一半）
        const lastAiModel = [...res.data.messages].reverse().find((m) => m.role !== 'user' && m.model)?.model;
        if (lastAiModel) {
          // 桌面端系统行在「平台模型」分组下（选项值为 platform: 前缀），裸 id 高亮不上
          const row = modelCatalog?.models?.find((m) => m.id === lastAiModel);
          setCurrentModel(isDesktop && row?.is_system ? `${PLATFORM_PREFIX}${lastAiModel}` : lastAiModel);
        }
      })
      .catch(() => {
        if (seq !== conversationLoadSeqRef.current) return; // 已切走，别误清新会话
        // 会话可能已被删除/服务端异常：清掉活动会话，保留本地消息作为回退
        setActiveConversationId(null);
        activeConvModeRef.current = 'unknown';
      });
    // modelCatalog 只作桌面端选项前缀的展示修正，不进依赖——目录晚到不应触发会话重拉
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConversationId, isLoggedIn, setActiveConversationId]);

  // 未登录/异常回退：本地消息快照存 localStorage（最近 20 条）；
  // 仅未登录时恢复——登录用户以服务端会话为准，避免旧快照盖住服务端数据。
  // 登录态不写快照：共享设备上 A 登出后其聊天记录不应明文留在本地被下一个人看到
  useEffect(() => {
    if (isLoggedIn) return;
    try {
      localStorage.setItem('chatLocalHistory', JSON.stringify(messages.slice(-20)));
    } catch {
      // ignore
    }
  }, [messages, isLoggedIn]);

  useEffect(() => {
    if (isAuthLoading || isLoggedIn) return;
    try {
      const raw = localStorage.getItem('chatLocalHistory');
      if (!raw) return;
      const saved = JSON.parse(raw) as Array<Omit<ChatMessage, 'timestamp'> & { timestamp: string }>;
      if (Array.isArray(saved) && saved.length > 0) {
        setMessages(saved.map((m) => ({ ...m, timestamp: new Date(m.timestamp) })));
      }
    } catch {
      // ignore
    }
    // 恢复本地草稿只在登录态变化时执行一次
  }, [isAuthLoading, isLoggedIn]);



  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // 智能体（agent 环）开关，三态循环：默认开（站内）；本地档（ollama lite）禁用——小模型不做工具调用
  const [agentMode, setAgentMode] = useState<AgentMode>('internal');
  const agentAllowed = !isLocalModel(currentModel);

  // 写工具确认卡回调：approve/reject 都走 agent-confirm 端点唤醒挂起的环
  const confirmAgentAction = async (pendingId: string, approve: boolean) => {
    try {
      const token = apiClient.getToken();
      const resp = await fetch('/api/v1/llm/agent-confirm', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify({ pending_id: pendingId, approve }),
      });
      if (!resp.ok) showToast('确认已过期，该操作已按取消处理', 'error');
    } catch {
      showToast('确认失败，请重试', 'error');
    }
  };

  const handleSend = async (overrideText?: unknown) => {
    // 「重来」直传文本（onClick 会把 MouseEvent 当第一个参数传进来，只认 string）
    const textOverride = typeof overrideText === 'string' ? overrideText : undefined;
    const messageToSend = textOverride ?? message;
    // 同步锁防重复发送（state 异步更新有延迟）
    if (abortControllerRef.current) return;
    if (!messageToSend.trim() && attachments.length === 0) return;

    // Check balance for non-local models before sending (use cached balance)
    // 平台模型（platform: 前缀）走云端余额，由云端 402/错误事件兜底；
    // 桌面端 BYOK 自付不扣平台余额——预检只对网页端平台计费模型有意义
    if (!isDesktop && !isLocalModel(currentModel) && !isPlatformModel(currentModel)) {
      // 仅余额已加载且确实不足才拦截；未加载/接口失败（balance 为 undefined）
      // 不踢充值页——与 LLMConnectionStatus 的口径一致，余额不足由服务端兜底
      if (balance && balance.balance < 0.1) {
        navigate('/topup');
        return;
      }
    }

    // 附件名以行追加到消息展示内容；文件正文由后端注入对话上下文
    const attachmentNote = attachments.length > 0
      ? `\n${attachments.map((a) => `[附件: ${a.name}]`).join('\n')}`
      : '';
    const userMsg: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: messageToSend + attachmentNote,
      timestamp: new Date(),
    };
    const aiMsgId = (Date.now() + 1).toString();
    // 智能体轮次在气泡上标注模式（站内/站外），让用户知道这轮是智能体跑的
    const agentLabel = agentMode !== 'off' && agentAllowed
      ? `智能体 · ${agentMode === 'external' ? '站外模式' : '站内模式'}`
      : undefined;
    const aiMsg: ChatMessage = { id: aiMsgId, role: 'ai', content: '', model: currentModel, timestamp: new Date(), isStreaming: true, agentLabel };
    setMessages((prev) => [...prev, userMsg, aiMsg]);
    setMessage('');
    setAttachments([]);
    setIsStreaming(true);
    setShowChatPanel(true);

    // 提升到 try 外：catch 里的断连自愈回拉要用它
    let conversationId: string | null = activeConversationId;
    try {
      // 智能体轮次必须落智能体会话（后端 400 硬口径）：当前不是 agent 会话就
      // 新建一个并切过去（原会话不动，普通对话随时可切回）
      const wantAgent = agentMode !== 'off' && agentAllowed;
      if (isLoggedIn && wantAgent && activeConvModeRef.current !== 'agent') {
        try {
          const res = await chatApi.createConversation(undefined, 'agent');
          conversationId = res.data.id;
          activeConvModeRef.current = 'agent';
          skipConversationLoadRef.current = conversationId;
          setActiveConversationId(conversationId);
          // 新会话从空开始，别把旧会话消息带进智能体上下文——但本轮刚追加的
          // user/ai 两条必须在场：整屏清空会把 aiMsgId 从 state 抹掉，后续流式
          // patch 全部落空=输入后闪一下空白、跑完也空白（09-23 站外模式实捕）
          setMessages([userMsg, aiMsg]);
        } catch {
          conversationId = null;
        }
      }
      // 对话落库：登录用户无活动会话则先创建再带上 conversation_id；
      // 创建失败不阻塞对话（本轮按旧行为不落库）
      if (isLoggedIn && !conversationId) {
        try {
          const res = await chatApi.createConversation();
          conversationId = res.data.id;
          activeConvModeRef.current = 'chat';
          skipConversationLoadRef.current = conversationId;
          setActiveConversationId(conversationId);
        } catch {
          conversationId = null;
        }
      }
      const token = apiClient.getToken();
      // 平台模型（platform: 前缀）：本地 /llm/chat 统一入口——本地 RAG 检索照常，
      // 模型执行由后端代理转发到绑定云端（云端按量扣费），全站各端点同理。
      const backendModel = isPlatformModel(currentModel)
        ? currentModel
        : getBackendModelId(currentModel, ollamaModel);
      // 停止按钮依赖这个 controller（此前从未创建/接线，停止形同虚设）
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      const doChat = (convId: string | null) => fetch('/api/v1/llm/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
        },
        signal: abortController.signal,
        body: JSON.stringify({
          message: userMsg.content,
          brain_side: effectiveBrain,
          history: messages
            .filter((m) => m.content.trim() && !m.isStreaming)
            .slice(-20) // 截断长对话历史，防请求体过大/超上下文
            .map((m) => ({
              role: m.role === 'ai' ? 'assistant' : m.role,
              content: m.content,
            })),
          preferred_model: backendModel,
          conversation_id: convId || undefined,
          // 智能体环：开关打开且非本地档才上送 agent_mode（后端不做 RAG 自动注入，
          // 智能体自主调工具）；站外另带 web_access（工具集含 web_fetch）。
          // 旧字段 agent（深度研究）后端保留但前端不再用
          agent_mode: agentMode !== 'off' && agentAllowed,
          web_access: agentMode === 'external',
          brain_style: effectiveBrain === 'personal' ? 'warm_personal' : effectiveBrain === 'network' ? 'objective_network' : 'balanced',
          attachments: attachments.map((a) => a.path).filter((p) => Boolean(p)),
        }),
      });
      let response = await doChat(conversationId);
      // 形态错位自愈（09-27 实捕）：智能体轮撞上普通会话吃后端 400「不是智能体
      // 会话」（会话切换瞬间形态 fetch 未落地、ref 残留上一会话的 agent 值所致）——
      // 新建智能体会话重发一次，与下方 404 同款口径
      if (response.status === 400 && wantAgent) {
        const errText = await response.clone().text().catch(() => '');
        if (errText.includes('不是智能体会话')) {
          let retryConvId: string | null = null;
          try {
            const res = await chatApi.createConversation(undefined, 'agent');
            retryConvId = res.data.id;
            activeConvModeRef.current = 'agent';
            skipConversationLoadRef.current = retryConvId;
            setActiveConversationId(retryConvId);
          } catch {
            retryConvId = null;
          }
          if (retryConvId) {
            response = await doChat(retryConvId);
            if (response.ok) conversationId = retryConvId; // 断连自愈/收尾要用新会话 id
          }
        }
      }
      // 会话在别处被删（多端/历史页清理）：后端 404「会话不存在」——脱离旧会话、
      // 新建一个自动重发一次；不拦会被错误映射误译成「LLM 服务接口未找到」（9/4 实捕，
      // 表面像模型路由挂了，实际是陈旧 conversation_id）
      if (response.status === 404 && conversationId) {
        const errText = await response.clone().text().catch(() => '');
        if (errText.includes('会话不存在')) {
          setActiveConversationId(null);
          activeConvModeRef.current = 'unknown';
          let retryConvId: string | null = null;
          try {
            const res = await chatApi.createConversation(undefined, wantAgent ? 'agent' : undefined);
            retryConvId = res.data.id;
            activeConvModeRef.current = wantAgent ? 'agent' : 'chat';
            skipConversationLoadRef.current = retryConvId;
            setActiveConversationId(retryConvId);
          } catch {
            retryConvId = null;
          }
          response = await doChat(retryConvId);
          if (response.ok) conversationId = retryConvId; // 断连自愈/收尾要用新会话 id
        }
      }

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      // SSE 协议层统一走 utils/chatStream（单一事实源，与 ChatPanel 共用）；
      // 下方只留 ChatInputBar 自己的编排：引用源标记 / factcheck 计数 / agent 轨迹
      let fullContent = '';
      let streamError = '';
      // 后端在流末尾（end 之前）下发 {"type":"sources","sources":[...]}，仅当引用源非空时才有此事件
      let receivedSources = false;
      // 规则版 FactCheck：sources 之后、end 之前下发，只取越界引用标记数
      let factcheckInvalidCount = 0;
      // 智能体 agent 环：逐轮工具轨迹（会话瞬态）+ 写确认卡状态
      let agentTrace: { round: number; name: string; summary: string; status: string }[] = [];
      const patchMsg = (patch: Partial<ChatMessage>) => {
        setMessages((prev) => prev.map((m) => (m.id === aiMsgId ? { ...m, ...patch } : m)));
      };

      const streamResult = await consumeChatStream(response, {
        onChunk: (full) => {
          setMessages((prev) =>
            prev.map((m) => (m.id === aiMsgId ? { ...m, content: full, isStreaming: true } : m))
          );
        },
        onReplace: (full) => {
          // Grounding 重答修正：数字/编号未在库内证据中找到时服务端重答，
          // 整体替换已流出的第一答（后端落库以重答为准，前端必须同步口径）
          setMessages((prev) =>
            prev.map((m) => (m.id === aiMsgId ? { ...m, content: full, isStreaming: true } : m))
          );
        },
        onSources: () => { receivedSources = true; },
        onFactcheck: (refs) => { factcheckInvalidCount = refs.length; },
        onStreamError: (msg) => { streamError = msg; },
        // 并发闸排队公告：占位移「排队中」（首个 chunk/replace 到来即被覆盖）
        onQueued: () => {
          patchMsg({ content: '排队中：当前对话请求较多，已自动排队，稍候即开始生成…', isStreaming: true });
        },
        onToolCall: (data) => {
          // agent 环：某轮发起工具调用
          agentTrace = [...agentTrace, { round: data.round ?? 0, name: data.name, summary: data.summary || '', status: 'running' }];
          patchMsg({ agentTrace });
        },
        onToolResult: (data) => {
          // 工具执行落地：关掉最近一条同名 running
          const idx = [...agentTrace].reverse().findIndex((t) => t.name === data.name && t.status === 'running');
          if (idx >= 0) {
            const real = agentTrace.length - 1 - idx;
            agentTrace = agentTrace.map((t, i) => (i === real ? { ...t, status: data.ok ? 'ok' : 'error' } : t));
            patchMsg({ agentTrace });
          }
        },
        onToolConfirmRequest: (data) => {
          // 写工具待确认：渲染确认卡（环在后端挂起等 agent-confirm）
          patchMsg({ pendingConfirm: { pending_id: data.pending_id, name: data.name, summary: data.summary || '' } });
        },
        onToolConfirmResult: (data) => {
          const idx = [...agentTrace].reverse().findIndex((t) => t.status === 'running');
          if (idx >= 0 && !data.approved) {
            const real = agentTrace.length - 1 - idx;
            agentTrace = agentTrace.map((t, i) => (i === real ? { ...t, status: 'rejected' } : t));
          }
          patchMsg({ pendingConfirm: null, agentTrace });
        },
        onAgentTrace: (data) => {
          // 环收尾：服务端权威轨迹 + 总轮次
          if (Array.isArray(data.trace)) agentTrace = data.trace as typeof agentTrace;
          patchMsg({ agentTrace, agentRounds: data.rounds, pendingConfirm: null });
        },
      });
      fullContent = streamResult.fullContent;
      streamError = streamError || streamResult.streamError;

      setMessages((prev) =>
        prev.map((m) =>
          m.id === aiMsgId
            ? {
                ...m,
                content: fullContent,
                isStreaming: false,
                // 空知识库引导（R04）：有正文但全程没收到引用源 → 标记，回答下方显示「去导入」。
                // 智能体轮次除外：agent 资料来自工具取证（站外还有联网搜索），本就没有 sources
                // 事件，「去导入」在这种场景是纯噪音（09-23 站外实捕）
                noSources: Boolean(fullContent.trim()) && !receivedSources && !wantAgent,
                factcheckInvalid: factcheckInvalidCount,
                pendingConfirm: null, // 流结束清确认卡（超时/断连兜底）
              }
            : m
        )
      );
      // 流内错误事件（如云端余额不足）：无正文时替换为错误提示
      if (streamError && !fullContent) {
        const hint = streamError.includes('余额不足')
          ? `${streamError}（平台模型按量扣费，请到 grzhishiku.com 充值云端余额）`
          : streamError;
        setMessages((prev) =>
          prev.map((m) => (m.id === aiMsgId ? { ...m, content: `⚠️ ${hint}`, isStreaming: false } : m))
        );
      }
      // 流式完成：会话的标题/updated_at/消息已落库，失效 chat 前缀查询让历史页刷新
      if (conversationId) {
        queryClient.invalidateQueries({ queryKey: ['chat'] });
      }
    } catch (error: any) {
      // 用户主动停止：保留已生成的部分内容，不当错误处理
      if (error?.name === 'AbortError') {
        setMessages((prev) =>
          prev.map((m) => (m.id === aiMsgId ? { ...m, isStreaming: false } : m))
        );
        return;
      }
      const errMsg = error?.message || '未知错误';

      // 会员门：外部模型需要 Pro 会员（¥9.9/月）→ 弹付费引导而不是普通错误
      if (errMsg.includes('403') && errMsg.includes('Pro 会员功能')) {
        setPaywallOpen(true);
        setMessages((prev) => prev.filter((m) => m.id !== aiMsgId));
        setIsStreaming(false);
        return;
      }

      // 断连自愈（9/4 外测实捕）：SSE 中途断连时后端往往已生成完成并落库，
      // 直接报错会诱导用户重复提交（历史上躺多条不同答案）。先回拉会话：
      // 服务端已有本轮 assistant 回复就直接补上，回拉不到才走错误提示。
      if (conversationId) {
        try {
          const res = await chatApi.getConversation(conversationId);
          const sentAt = userMsg.timestamp.getTime();
          const recovered = [...res.data.messages].reverse().find(
            (m) => m.role !== 'user' && m.content?.trim() && m.created_at &&
              // 服务端 created_at 是 UTC 无时区后缀，补 'Z' 再解析，避免被当本地时间
              new Date(m.created_at.endsWith('Z') ? m.created_at : m.created_at + 'Z').getTime() >= sentAt - 5000
          );
          if (recovered) {
            setMessages((prev) =>
              prev.map((m) => (m.id === aiMsgId
                ? { ...m, content: recovered.content, model: recovered.model || m.model, isStreaming: false }
                : m))
            );
            queryClient.invalidateQueries({ queryKey: ['chat'] });
            return;
          }
        } catch {
          // 回拉失败，落回错误提示
        }
      }

      let displayMsg = '抱歉，连接出现问题。';

      if (errMsg.includes('401') || errMsg.includes('Not authenticated') || errMsg.includes('Invalid token')) {
        displayMsg = '请先登录后再使用 AI 助手。';
      } else if (errMsg.includes('会话不存在')) {
        displayMsg = '会话已在别处被删除，请开新对话重试。';
      } else if (errMsg.includes('404') || errMsg.includes('Not Found')) {
        displayMsg = 'LLM 服务接口未找到，请检查后端是否已更新。';
      } else if (errMsg.includes('Failed to fetch') || errMsg.includes('NetworkError')) {
        displayMsg = '网络连接失败，请检查后端服务是否已启动。';
      } else {
        // 断连但后端可能仍在跑/已落库：如实提示去历史页核对，别急着重发（重复落答案实捕）
        displayMsg = `服务错误: ${errMsg}（如重发前请先看对话历史，答案可能已生成）`;
      }

      setMessages((prev) =>
        prev.map((m) =>
          m.id === aiMsgId
            ? { ...m, content: displayMsg, isStreaming: false }
            : m
        )
      );
      showToast(displayMsg, 'error');
    } finally {
      abortControllerRef.current = null;
      setIsStreaming(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // isComposing 守卫：中文输入法选词确认的 Enter 不触发发送
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleSend();
    }
  };

  // ---- 桌面端本地语音（开源模型，离线识别）----

  const refreshVoiceModels = useCallback(async () => {
    try {
      setVoiceModels(await listSpeechModels());
    } catch {
      // 云端/网页端 400 或网络异常，面板里给提示即可
    }
  }, []);

  // 下载中的模型轮询进度；全部结束后停表
  const startVoicePolling = useCallback(() => {
    if (voicePollRef.current) return;
    voicePollRef.current = setInterval(async () => {
      const models = await listSpeechModels().catch(() => null);
      if (!models) return;
      setVoiceModels(models);
      const active = models.some(
        (m) => !m.downloaded && m.download?.status === 'downloading'
      );
      if (!active && voicePollRef.current) {
        clearInterval(voicePollRef.current);
        voicePollRef.current = null;
      }
    }, 1000);
  }, []);

  const handleVoiceDownload = async (modelId: string) => {
    try {
      await startSpeechModelDownload(modelId);
      startVoicePolling();
      refreshVoiceModels();
    } catch (e: any) {
      showToast(e?.response?.data?.detail || '模型下载启动失败', 'error');
    }
  };

  const pickVoiceModel = (modelId: string) => {
    setVoiceModelId(modelId);
    try {
      localStorage.setItem('voice_model_id', modelId);
    } catch {
      // ignore
    }
  };

  // 桌面端点击麦克风：无已下载模型 → 弹选择面板（含下载提示）；有 → 直接录音
  const handleDesktopVoiceToggle = async () => {
    if (isRecording) {
      setIsRecording(false);
      const rec = wavRecorderRef.current;
      wavRecorderRef.current = null;
      if (!rec) return;
      const wav = rec.stop();
      setIsTranscribing(true);
      try {
        const { text } = await transcribeAudio(wav, voiceModelId || undefined);
        if (text) {
          setMessage((prev) => (prev ? prev.trimEnd() + ' ' : '') + text);
        } else {
          showToast('没有识别到语音内容', 'error');
        }
      } catch (e: any) {
        showToast(e?.response?.data?.detail || '语音识别失败', 'error');
      } finally {
        setIsTranscribing(false);
      }
      return;
    }

    let models = voiceModels;
    try {
      models = await listSpeechModels();
      setVoiceModels(models);
    } catch (e: any) {
      showToast(e?.response?.data?.detail || '语音服务不可用', 'error');
      return;
    }
    const downloaded = models.filter((m) => m.downloaded);
    if (downloaded.length === 0) {
      setShowVoicePanel(true); // 引导用户自选模型并下载
      return;
    }
    if (!voiceModelId || !downloaded.some((m) => m.id === voiceModelId)) {
      pickVoiceModel(downloaded[0].id);
    }
    try {
      const rec = new WavRecorder();
      await rec.start();
      wavRecorderRef.current = rec;
      setIsRecording(true);
    } catch {
      showToast('麦克风不可用，请在系统设置中授权', 'error');
    }
  };

  // 语音输入：桌面端走本地开源模型；网页端用 Web Speech API（中文），不支持的环境明确降级提示
  const handleVoiceToggle = () => {
    if (isDesktop) {
      handleDesktopVoiceToggle();
      return;
    }
    if (isRecording) {
      recognitionRef.current?.stop();
      setIsRecording(false);
      return;
    }

    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) {
      showToast('当前环境不支持语音输入，请使用 Chrome 或桌面端', 'error');
      return;
    }

    const rec = new SR();
    rec.lang = 'zh-CN';
    rec.continuous = true;
    rec.interimResults = true;

    rec.onresult = (e: any) => {
      let finalText = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) finalText += e.results[i][0].transcript;
      }
      if (finalText) {
        setMessage((prev) => (prev ? prev.trimEnd() + ' ' : '') + finalText.trim());
      }
    };
    rec.onerror = (e: any) => {
      setIsRecording(false);
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
        showToast('麦克风权限被拒绝，请在系统/浏览器中授权', 'error');
      } else if (e.error !== 'aborted') {
        showToast(`语音识别失败：${e.error}`, 'error');
      }
    };
    rec.onend = () => setIsRecording(false);

    recognitionRef.current = rec;
    try {
      rec.start();
      setIsRecording(true);
    } catch {
      setIsRecording(false);
      showToast('无法启动语音识别', 'error');
    }
  };

  // 卸载时停止识别/录音/轮询
  useEffect(() => {
    return () => {
      recognitionRef.current?.stop();
      wavRecorderRef.current?.cancel();
      if (voicePollRef.current) clearInterval(voicePollRef.current);
    };
  }, []);

  // 全局快捷键 Ctrl+Shift+J（桌面端）：唤起对话面板并聚焦输入框
  useEffect(() => {
    return window.psbDesktop?.onQuickChat?.(() => {
      setShowChatPanel(true);
      setTimeout(() => inputRef.current?.focus(), 50);
    });
  }, [setShowChatPanel]);

  const handleAttachment = async (type: string) => {
    if (type === '文件' || type === '图片') {
      // 文件/图片附件：桌面端走系统文件选择框，本地路径交后端解析（图片走 OCR）
      if (!isDesktop || !window.psbDesktop?.pickFile) {
        showToast(`${type}附件仅支持桌面端`, 'error');
        setShowAttachmentMenu(false);
        return;
      }
      try {
        const isImg = type === '图片';
        const paths = await window.psbDesktop.pickFile(`选择${type}`, isImg ? 'image' : undefined);
        if (paths && paths.length > 0) {
          setAttachments((prev) => [
            ...prev,
            ...paths.map((path) => ({
              name: path.split(/[\\/]/).pop() || path,
              path,
            })),
          ]);
        }
      } catch {
        showToast('选择文件失败', 'error');
      }
    } else if (type === '链接') {
      // 链接附件：两端可用，后端 SSRF 校验+readability 抽正文
      setLinkUrl('');
      setShowLinkInput(true);
    } else if (type === '笔记') {
      // 笔记附件：两端可用，挑选库内笔记，后端按 id 取正文
      setNoteQuery('');
      setShowNotePicker(true);
    }
    setShowAttachmentMenu(false);
  };

  const addLinkAttachment = () => {
    const url = linkUrl.trim();
    if (!/^https?:\/\/\S+$/.test(url)) {
      showToast('请输入 http(s) 链接', 'error');
      return;
    }
    setAttachments((prev) => [...prev, { name: url.length > 40 ? url.slice(0, 40) + '…' : url, path: `url:${url}` }]);
    setShowLinkInput(false);
    setLinkUrl('');
  };

  const handleExport = (format: 'json' | 'markdown') => {
    if (messages.length === 0) return;
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    if (format === 'json') {
      const data = {
        exportedAt: new Date().toISOString(),
        model: currentModel,
        messages: messages.map((m) => ({
          role: m.role,
          content: m.content,
          model: m.model,
          timestamp: m.timestamp.toISOString(),
        })),
      };
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `chat-export-${timestamp}.json`;
      a.click();
      URL.revokeObjectURL(url);
      showToast('对话已导出为 JSON', 'success');
    } else {
      let md = `# 对话记录\n\n**导出时间**: ${new Date().toLocaleString('zh-CN')}\n**使用模型**: ${LLM_MODEL_MAP[currentModel]?.name || currentModel}\n\n---\n\n`;
      messages.forEach((m) => {
        const exportModel = (m.model || '未知').replace(/^platform:/, '');
        md += m.role === 'user' ? `**用户**: ${m.content}\n\n` : `**AI** (${exportModel}): ${m.content}\n\n`;
      });
      const blob = new Blob([md], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `chat-export-${timestamp}.md`;
      a.click();
      URL.revokeObjectURL(url);
      showToast('对话已导出为 Markdown', 'success');
    }
  };

  const modelInfo = LLM_MODEL_MAP[currentModel];
  // 展示用模型名：平台模型查目录名（剥 platform: 前缀），查不到才回退裸 id
  const catalogName = useModelDisplayName(currentModel);
  const displayModelName = modelInfo?.name || (isPlatformModel(currentModel) ? catalogName : currentModel);

  const handleModelChange = async (modelId: string) => {
    const prevModel = currentModel;
    setCurrentModel(modelId);
    if (!isLoggedIn) {
      onLoginClick?.();
      return;
    }
    // 平台模型只是本次聊天的通道选择（走云端转发），不写入全局 active provider
    if (isPlatformModel(modelId)) {
      queryClient.invalidateQueries({ queryKey: ['llmStatus'] });
      return;
    }
    // 解析目标 (provider, model)：系统行写目录 id、BYOK 行写 provider_model_id
    let nextProvider: string;
    let nextModel: string;
    const config = LLM_MODEL_MAP[modelId];
    if (config) {
      nextProvider = config.provider;
      nextModel = config.model;
    } else if (modelId.startsWith('ollama-')) {
      nextProvider = 'ollama';
      // 剥前缀后归一为冒号形（qwen3.5:0.8b），与 store 落库形态一致——
      // 否则下方防乒乓守卫拿短横形（qwen3.5-0.8b）比冒号形，同模型也判不等，
      // 误写后端；反向则吞掉真实点击（点一次不跳、点两次才跳）
      nextModel = normalizeOllamaModelName(modelId);
    } else {
      const row = modelCatalog?.models?.find((m) => m.id === modelId);
      if (row) {
        nextProvider = row.provider;
        nextModel = row.is_system ? row.id : row.provider_model_id;
      } else {
        nextProvider = modelId.split('-')[0] || 'deepseek';
        nextModel = modelId;
      }
    }
    // 挂载跟随/兜底填充的接管目标就是当前全局选择（consoleDefaultId 本就由 store 推导）：
    // 只改本地显示，不再回写服务器——否则初次挂载会把本地默认 ollama 误写到云端 active，
    // 触发 status 失效回读，两个方向互相覆盖形成乒乓
    if (nextProvider === activeProvider && nextModel === activeModel) return;
    // 失败兜底：setActiveProvider 会写后端，异常时 toast 并回滚本地选型，
    // 避免 UI 显示与后端 active 漂移
    setActiveProvider(nextProvider, nextModel).catch(() => {
      setCurrentModel(prevModel);
      showToast('切换模型失败，请重试', 'error');
    });
    queryClient.invalidateQueries({ queryKey: ['llmStatus'] });
  };

  const renderModelRow = () => (
    <div className="px-4 pt-2 pb-1 max-w-4xl mx-auto">
      <div className="flex items-center gap-3">
        <ModelSelector
          value={currentModel}
          onChange={handleModelChange}
          taskType="chat"
          className="w-56"
          disabled={isStreaming}
          allowPlatform
          autoSync={!conversationLocked}
          mountFollow
        />
        {/* 智能体开关（三态循环：关 → 站内 → 站外）：本地 lite 档禁用（小模型不做工具调用）。
            智能体多轮工具调用 = 多倍 token 消耗，hover 文案如实告知模式与计费口径 */}
        <button
          onClick={() => agentAllowed && setAgentMode(AGENT_MODE_CYCLE[agentMode])}
          disabled={isStreaming || !agentAllowed}
          className={`flex items-center gap-1 px-2.5 py-1.5 rounded-[2px] text-xs font-medium border transition-all shrink-0 ${
            agentMode === 'internal'
              ? 'bg-info/15 text-info border-info/40'
              : agentMode === 'external'
                ? 'bg-fusion-primary/15 text-fusion-primary border-fusion-primary/40'
                : 'text-text-secondary border-border-color hover:bg-bg-hover'
          } ${!agentAllowed ? 'opacity-50 cursor-not-allowed' : ''}`}
          title={agentAllowed
            ? '智能体：自主调用工具、多轮执行后回答。站内=知识库+站内工具；站外=另含联网抓取（按实际消耗按轮计费）'
            : '智能体需要 BYOK 或平台模型（本地小模型不支持工具调用）'}
        >
          <Bot size={13} />
          <span className="hidden sm:inline">
            {agentMode === 'internal' ? '智能体·站内' : agentMode === 'external' ? '智能体·站外' : '智能体'}
          </span>
        </button>
        <div className="hidden sm:block flex-1 min-w-0">
          <LLMCostBadge
            modelId={currentModel}
            inputText={message}
            outputTokenEstimate={200}
          />
        </div>
      </div>
      {/* Mobile cost badge */}
      <div className="sm:hidden mt-2">
        <LLMCostBadge
          modelId={currentModel}
          inputText={message}
          outputTokenEstimate={200}
        />
      </div>
      {/* Model capability tags */}
      {modelInfo && (
        <div className="hidden sm:flex items-center gap-1.5 overflow-hidden mt-2">
          {modelInfo.tags.map((tag) => (
            <span key={tag} className="text-[10px] px-2 py-0.5 rounded-full bg-bg-secondary text-text-secondary border border-border-color whitespace-nowrap">
              {tag}
            </span>
          ))}
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-bg-secondary text-text-secondary border border-border-color whitespace-nowrap">
            上下文 {modelInfo.context}
          </span>
        </div>
      )}
    </div>
  );

  const renderInputRow = () => (
    // 输入行 w-full + max-w-2xl 居中（约 2/3 宽）。坑：flex 列容器里 auto 外边距
    // 会吸收空闲并阻止拉伸，只写 max-w-*+mx-auto 不收显式宽度会塌成内容固有宽
    <div className="flex items-center gap-2 sm:gap-3 px-4 py-3 w-full max-w-2xl mx-auto relative">
      {/* Brain Indicator with Quick Switch */}
      <div className="relative shrink-0">
        <button
          onClick={() => setShowBrainSwitcher(!showBrainSwitcher)}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-[2px] text-xs font-medium border transition-all hover:bg-bg-hover ${
            effectiveBrain === 'personal' ? 'bg-personal-primary/10 text-personal-primary border-personal-primary/30 hover:border-personal-primary/50' :
            effectiveBrain === 'network' ? 'bg-network-primary/10 text-network-primary border-network-primary/30 hover:border-network-primary/50' :
            'bg-fusion-primary/10 text-fusion-primary border-fusion-primary/30 hover:border-fusion-primary/50'
          }`}
          title={`当前: ${brainLabel} (点击切换)`}
        >
          <BrainIcon size={14} />
          <span className="hidden sm:inline">{brainLabel}</span>
        </button>

        <AnimatePresence>
          {showBrainSwitcher && (
            <motion.div
              initial={{ opacity: 0, y: 5, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 5, scale: 0.95 }}
              className="absolute bottom-full left-0 mb-2 w-40 glass rounded-[2px] border border-border-color py-1 z-50"
            >
              {[
                { id: 'personal', label: '个人脑', icon: Home, color: 'text-personal-primary', bg: 'hover:bg-personal-primary/10' },
                { id: 'network', label: '网络脑', icon: Globe, color: 'text-network-primary', bg: 'hover:bg-network-primary/10' },
                { id: 'both', label: '双脑融合', icon: Brain, color: 'text-fusion-primary', bg: 'hover:bg-fusion-primary/10' },
              ].map((b) => {
                const Icon = b.icon;
                const isActive = effectiveBrain === b.id;
                return (
                  <button
                    key={b.id}
                    onClick={() => {
                      // 失败兜底：本地 brainSide 只在 onSuccess 时同步，
                      // 异常时本地状态天然保持原值，只需 toast 提示
                      switchBrain(b.id as BrainSide).catch(() => {
                        showToast('切换脑侧失败，请重试', 'error');
                      });
                      setShowBrainSwitcher(false);
                    }}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-left transition-colors ${
                      isActive ? `${b.bg} ${b.color} bg-bg-secondary` : 'text-text-primary hover:bg-bg-hover'
                    }`}
                  >
                    <Icon size={14} className={isActive ? b.color : 'text-text-secondary'} />
                    <span className="text-xs">{b.label}</span>
                    {isActive && <div className={`ml-auto w-1.5 h-1.5 rounded-full ${b.color.replace('text-', 'bg-')}`} />}
                  </button>
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Voice */}
      <div className="relative shrink-0 flex items-center">
        <button onClick={handleVoiceToggle} className={`p-2.5 rounded-[2px] transition-all ${isRecording ? 'bg-danger/10 text-danger animate-pulse' : 'hover:bg-bg-hover text-text-secondary hover:text-danger'}`}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
        </button>
        {isDesktop && (
          <button
            onClick={() => {
              const next = !showVoicePanel;
              setShowVoicePanel(next);
              if (next) refreshVoiceModels();
            }}
            title="选择语音模型"
            className="p-0.5 rounded-[2px] text-text-muted hover:text-text-secondary hover:bg-bg-hover transition-all"
          >
            <ChevronDown size={12} />
          </button>
        )}

        {/* 语音模型选择面板：首次使用下载开源模型，识别全程离线。
            portal 到 body + z-[70]：对话面板（fixed z-50）打开时弹层不能被它盖住 */}
        {createPortal(
          <AnimatePresence>
            {isDesktop && showVoicePanel && (
            <motion.div
              initial={{ opacity: 0, y: 5, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 5, scale: 0.95 }}
              className="fixed bottom-24 left-4 w-[calc(100vw-2rem)] max-w-72 glass rounded-[2px] border border-border-color p-3 z-[70] shadow-xl"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-text-primary">本地语音模型</span>
                <button onClick={() => setShowVoicePanel(false)} className="text-text-muted hover:text-text-secondary">
                  <X size={12} />
                </button>
              </div>
              <p className="text-[11px] text-text-muted mb-2 leading-relaxed">
                首次使用需先下载一个模型；识别在本机离线完成，语音不会上传。
              </p>
              {voiceModels.length === 0 && (
                <p className="text-[11px] text-text-muted">模型列表加载失败，请确认后端已启动。</p>
              )}
              <div className="space-y-2">
                {voiceModels.map((m) => {
                  const dl = m.download;
                  const downloading = !m.downloaded && dl?.status === 'downloading';
                  const failed = !m.downloaded && dl?.status === 'error';
                  return (
                    <div key={m.id} className="border border-border-color rounded-[2px] p-2">
                      <div className="flex items-center gap-2">
                        {m.downloaded ? (
                          <button onClick={() => pickVoiceModel(m.id)} className="flex items-center gap-2 min-w-0 text-left">
                            <div className={`w-2 h-2 rounded-full shrink-0 ${voiceModelId === m.id ? 'bg-accent' : 'bg-bg-tertiary border border-border-color'}`} />
                            <span className="text-xs text-text-primary truncate">{m.name}</span>
                          </button>
                        ) : (
                          <span className="text-xs text-text-primary truncate">{m.name}</span>
                        )}
                        <span className="ml-auto text-[10px] text-text-muted shrink-0">
                          {m.downloaded ? '已下载' : `约 ${Math.round(m.size_bytes / 1024 / 1024)}MB`}
                        </span>
                      </div>
                      <p className="text-[11px] text-text-muted mt-0.5">{m.desc}</p>
                      {!m.downloaded && !downloading && (
                        <button
                          onClick={() => handleVoiceDownload(m.id)}
                          className="mt-1.5 flex items-center gap-1 text-[11px] text-accent hover:underline"
                        >
                          <Download size={11} /> {failed ? `下载失败，重试（${dl?.error || '网络错误'}）` : '下载'}
                        </button>
                      )}
                      {downloading && (
                        <div className="mt-1.5">
                          <div className="h-1 bg-bg-tertiary rounded-full overflow-hidden">
                            <div className="h-full bg-accent transition-all" style={{ width: `${dl?.percent || 0}%` }} />
                          </div>
                          <p className="text-[10px] text-text-muted mt-0.5">
                            下载中 {dl?.percent || 0}%
                            {dl?.total ? `（${Math.round((dl?.downloaded || 0) / 1024 / 1024)}/${Math.round(dl.total / 1024 / 1024)}MB）` : ''}
                            ，完成后即可使用
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </motion.div>
          )}
          </AnimatePresence>,
          document.body
        )}
      </div>

      <AnimatePresence>
        {(isRecording || isTranscribing) && (
          <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }} className="absolute left-10 bottom-full mb-2 px-3 py-1.5 bg-danger/10 border border-danger/30 rounded-[2px] text-xs text-danger">
            {isTranscribing ? '识别中，请稍候...' : <>正在录音，点击麦克风结束 <span className="animate-pulse">●</span></>}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Input：多行自适应（Enter 发送 / Shift+Enter 换行），高度由上面的 effect 按内容撑开 */}
      <div className="flex-1 relative">
        {isLoggedIn ? (
          <textarea
            ref={inputRef}
            rows={3}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={`与 ${effectiveBrain === 'personal' ? '个人脑' : effectiveBrain === 'network' ? '网络脑' : '双脑'} 对话...（Shift+Enter 换行）`}
            className="w-full bg-bg-secondary border border-border-color rounded-[2px] px-4 py-2.5 text-sm text-text-primary placeholder-text-muted focus:outline-none focus:border-info/60 focus:bg-bg-secondary transition-all shadow-inner resize-none overflow-y-auto leading-relaxed"
            disabled={isStreaming}
          />
        ) : (
          <button
            onClick={() => onLoginClick ? onLoginClick() : navigate('/welcome')}
            className="w-full bg-bg-secondary border border-border-color rounded-[2px] px-4 py-2.5 text-sm text-text-secondary text-left hover:border-info/30 hover:text-text-primary transition-colors flex items-center gap-2"
          >
            <LogIn className="w-4 h-4" />
            请登录后使用 AI 对话
          </button>
        )}
      </div>

      {/* Attachment */}
      <div className="relative shrink-0">
        <button onClick={() => setShowAttachmentMenu(!showAttachmentMenu)} className="p-2.5 rounded-[2px] hover:bg-bg-tertiary text-text-secondary transition-colors">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
        </button>
        <AnimatePresence>
          {showAttachmentMenu && (
            <motion.div initial={{ opacity: 0, y: 5, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 5, scale: 0.95 }} className="absolute bottom-full right-0 mb-2 w-48 glass rounded-[2px] border border-border-color py-2 z-50">
              {['文件', '图片', '链接', '笔记'].map((label) => (
                <button key={label} onClick={() => handleAttachment(label)} className="w-full text-left px-4 py-2.5 text-sm text-text-secondary hover:bg-bg-hover hover:text-text-primary transition-colors">{label}</button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* 链接附件输入弹层（回形针「链接」） */}
        {showLinkInput && (
          <div className="absolute bottom-full right-0 mb-2 w-80 glass rounded-[2px] border border-border-color p-3 z-50">
            <div className="text-xs text-text-secondary mb-2">粘贴链接，发送时后端抓取正文（SSRF 安全校验）</div>
            <input
              autoFocus
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') addLinkAttachment(); if (e.key === 'Escape') setShowLinkInput(false); }}
              placeholder="https://…"
              className="w-full px-2.5 py-1.5 text-sm bg-bg-primary border border-border-color rounded-[2px] text-text-primary focus:outline-none focus:border-accent"
            />
            <div className="flex justify-end gap-2 mt-2">
              <button onClick={() => setShowLinkInput(false)} className="px-2.5 py-1 text-xs text-text-secondary hover:bg-bg-hover rounded-[2px]">取消</button>
              <button onClick={addLinkAttachment} className="px-2.5 py-1 text-xs bg-accent text-white rounded-[2px] hover:opacity-90">添加</button>
            </div>
          </div>
        )}
      </div>

      {/* Send / Stop */}
      <button
        onClick={isStreaming ? () => { abortControllerRef.current?.abort(); setIsStreaming(false); } : handleSend}
        disabled={!isLoggedIn || (!isStreaming && !message.trim() && attachments.length === 0)}
        className={`shrink-0 p-2.5 rounded-[2px] transition-all shadow-sm ${
          isStreaming ? 'bg-danger hover:bg-danger/80 text-white shadow-danger/20' :
          !isLoggedIn ? 'bg-bg-tertiary text-text-secondary cursor-not-allowed' :
          message.trim() || attachments.length > 0 ? 'bg-accent hover:bg-[var(--accent-hover)] text-white shadow-info/20' : 'bg-bg-tertiary text-text-secondary cursor-not-allowed'
        }`}
      >
        {isStreaming ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="6" y="6" width="12" height="12" rx="2"/></svg>
        ) : (
          <Send size={20} />
        )}
      </button>
    </div>
  );


  // 悬浮球形态：不再占 main 列底部布局空间，全部内容均为 fixed/portal 浮层
  return (
    <>
      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className={`fixed top-4 right-4 z-[60] px-4 py-3 rounded-[2px] border ${
              toast.type === 'success'
                ? 'bg-success/20 border-success/30 text-success'
                : 'bg-danger/20 border-danger/30 text-danger'
            }`}
          >
            <div className="flex items-center gap-2">
              {toast.type === 'success' ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
              <span className="text-sm">{toast.message}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 会员付费引导（外部模型 403） */}
      <PaywallModal open={paywallOpen} onClose={() => setPaywallOpen(false)} />

      {/* 笔记挑选弹窗（回形针「笔记」）：搜索库内笔记，选中作为附件（后端按 id 取正文） */}
      {showNotePicker && (
        <div className="fixed inset-0 z-50 flex justify-center items-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowNotePicker(false)} />
          <div className="relative w-full max-w-md glass rounded-[2px] border border-border-color p-4">
            <div className="text-sm text-text-primary mb-2">选择一条笔记作为附件</div>
            <input
              autoFocus
              value={noteQuery}
              onChange={(e) => setNoteQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Escape') setShowNotePicker(false); }}
              placeholder="搜索笔记标题/内容…"
              className="w-full px-2.5 py-1.5 text-sm bg-bg-primary border border-border-color rounded-[2px] text-text-primary focus:outline-none focus:border-accent"
            />
            <div className="mt-2 max-h-64 overflow-y-auto divide-y divide-border-color/50">
              {(notePickerResults || []).map((n: Note) => (
                <button
                  key={n.id}
                  onClick={() => {
                    setAttachments((prev) => [...prev, { name: n.title, path: `note:${n.id}` }]);
                    setShowNotePicker(false);
                    showToast(`已附加笔记「${n.title}」`, 'success');
                  }}
                  className="w-full text-left px-2 py-2.5 hover:bg-bg-hover transition-colors"
                >
                  <div className="text-sm text-text-primary truncate">{n.title}</div>
                  {n.updated_at && <div className="text-[10px] text-text-muted mt-0.5">{new Date(n.updated_at).toLocaleDateString()}</div>}
                </button>
              ))}
              {notePickerResults && notePickerResults.length === 0 && (
                <div className="px-2 py-6 text-center text-xs text-text-muted">没有匹配的笔记</div>
              )}
            </div>
            <div className="flex justify-end mt-2">
              <button onClick={() => setShowNotePicker(false)} className="px-2.5 py-1 text-xs text-text-secondary hover:bg-bg-hover rounded-[2px]">取消</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Chat Panel：portal 到 body——main 列祖先有 relative z-10 层叠上下文，
          全屏态 z-50 困在其中会被顶栏（z-40）压住盖不着（全屏实捕：
          面板头部被顶栏遮挡、按钮点不到） */}
      {createPortal(
      <AnimatePresence>
        {showChatPanel && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-black/60"
              onClick={() => setShowChatPanel(false)}
            />
            {/* Popup container */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className={`fixed inset-0 z-50 flex justify-center items-center pointer-events-none ${chatFullscreen ? 'p-0' : 'p-4'}`}
              onClick={(e) => {
                if (e.target === e.currentTarget) setShowChatPanel(false);
              }}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className={`glass-strong border border-border-color overflow-hidden flex flex-col pointer-events-auto ${
                  chatFullscreen
                    ? 'w-full h-full max-w-none max-h-none rounded-none border-0'
                    : 'w-11/12 sm:w-2/3 max-w-5xl h-[85vh] rounded-[2px]'
                }`}
              >
              {/* Panel Header */}
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-border-color bg-black/30 shrink-0">
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className="text-xs font-semibold text-text-primary">AI 助手</span>
                  <span className="text-[10px] text-text-secondary truncate">{displayModelName}</span>
                  <div className="hidden sm:flex items-center gap-1">
                    {modelInfo?.tags.slice(0, 2).map((tag) => (
                      <span key={tag} className="text-[9px] px-1.5 py-0.5 rounded-full bg-bg-secondary text-text-secondary border border-border-color">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-0.5">
                  {/* 历史入口：跳 /chat 会话列表 */}
                  <button
                    onClick={() => navigate('/chat')}
                    className="p-1.5 rounded-[2px] hover:bg-bg-tertiary text-text-secondary hover:text-text-primary transition-colors"
                    title="对话历史"
                  >
                    <History size={14} />
                  </button>
                  {/* 新对话：清空本地消息并脱离当前会话，下一条消息自动新建会话 */}
                  <button
                    onClick={() => {
                      setMessages([]);
                      setActiveConversationId(null);
                    }}
                    className="p-1.5 rounded-[2px] hover:bg-bg-tertiary text-text-secondary hover:text-text-primary transition-colors"
                    title="新对话"
                  >
                    <MessageSquarePlus size={14} />
                  </button>
                  {/* Export：点击展开 + 外部点击关闭（血泪#23 再次实捕：hover 下拉
                      mt-1 的 4px 间隙断链——鼠标从按钮移向菜单必过空隙，hover 丢失
                      菜单消失，JSON/Markdown 永远点不到） */}
                  <div className="relative">
                    <button
                      onClick={() => setShowExportMenu((v) => !v)}
                      className="p-1.5 rounded-[2px] hover:bg-bg-tertiary text-text-secondary hover:text-text-primary transition-colors"
                      title="导出对话"
                    >
                      <Download size={14} />
                    </button>
                    {showExportMenu && (
                      <>
                        <div className="fixed inset-0 z-40 cursor-default" onClick={() => setShowExportMenu(false)} />
                        <div className="absolute right-0 top-full mt-1 flex flex-col gap-1 glass rounded-[2px] border border-border-color p-1 z-50 min-w-[120px]">
                          <button
                            onClick={() => { setShowExportMenu(false); handleExport('json'); }}
                            className="flex items-center gap-2 px-3 py-1.5 text-xs text-text-primary hover:bg-bg-hover rounded transition-colors"
                          >
                            <FileJson size={12} />
                            JSON
                          </button>
                          <button
                            onClick={() => { setShowExportMenu(false); handleExport('markdown'); }}
                            className="flex items-center gap-2 px-3 py-1.5 text-xs text-text-primary hover:bg-bg-hover rounded transition-colors"
                          >
                            <FileText size={12} />
                            Markdown
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      setMessages([]);
                      setActiveConversationId(null);
                    }}
                    className="p-1.5 rounded-[2px] hover:bg-bg-tertiary text-text-secondary hover:text-danger transition-colors"
                    title="清空对话"
                  >
                    <Trash2 size={14} />
                  </button>
                  <button
                    onClick={() => setChatFullscreen((v) => !v)}
                    className="p-1.5 rounded-[2px] hover:bg-bg-tertiary text-text-secondary hover:text-text-primary transition-colors"
                    title={chatFullscreen ? '退出全屏' : '全屏放大'}
                  >
                    {chatFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
                  </button>
                  <button
                    onClick={() => setShowChatPanel(false)}
                    className="p-1.5 rounded-[2px] hover:bg-bg-tertiary text-text-secondary hover:text-text-primary transition-colors"
                    title="收起"
                  >
                    <ChevronDown size={16} />
                  </button>
                  <button
                    onClick={() => setShowChatPanel(false)}
                    className="p-1.5 rounded-[2px] hover:bg-bg-tertiary text-text-secondary hover:text-text-primary transition-colors"
                    title="关闭"
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>

              {/* Messages：面板固定 85vh（h-auto 会让空对话塌回矮弹窗），flex-1 吃满剩余 */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0">
                {messages.map((msg) => (
                  <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className={`group relative max-w-[92%] sm:max-w-[85%] min-w-0 px-4 py-2.5 rounded-[2px] text-sm leading-relaxed ${
                        msg.role === 'user'
                          ? 'bg-accent text-white rounded-br-md'
                          : 'bg-bg-secondary border border-border-color text-text-primary rounded-bl-md shadow-sm'
                      }`}
                    >
                      {/* 复制整条回答：hover 显示，复制 msg.content 原文；绝对定位不挤确认卡/轨迹卡 */}
                      {msg.role === 'ai' && !msg.isStreaming && !!msg.content && (
                        <div className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                          <CopyButton text={msg.content} title="复制回答" />
                        </div>
                      )}
                      {/* 用户消息 hover 操作（09-25）：复制问题 + 重来（原文重发一轮）；
                          重来在流式进行中禁用，防和 abortController 锁打架 */}
                      {msg.role === 'user' && !!msg.content && (
                        <div className="absolute -top-2 left-1 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 bg-bg-secondary border border-border-color rounded-[2px] px-1 py-0.5 shadow-sm">
                          <CopyButton text={msg.content} title="复制问题" />
                          <button
                            onClick={() => handleSend(msg.content)}
                            disabled={isStreaming}
                            title="重来（把这个问题重发一轮）"
                            className="p-1 text-text-secondary hover:text-accent disabled:opacity-40"
                          >
                            <RotateCcw size={12} />
                          </button>
                        </div>
                      )}
                      {/* 智能体轮次的模式标注（站内/站外），仅本轮新消息带 */}
                      {msg.role === 'ai' && msg.agentLabel && (
                        <div className="text-[10px] text-info mb-1">{msg.agentLabel}</div>
                      )}
                      {msg.role === 'ai'
                        ? <MarkdownText content={msg.content} />
                        : <div className="whitespace-pre-wrap break-keep">{msg.content}</div>}
                      {/* 智能体轨迹：agent 环逐轮工具调用（可折叠）+ 写操作确认卡 */}
                      {msg.role === 'ai' && ((msg.agentTrace?.length ?? 0) > 0 || msg.pendingConfirm) && (
                        <div className="mb-2 rounded-[2px] border border-border-color/60 bg-bg-primary/40 text-xs">
                          {(msg.agentTrace?.length ?? 0) > 0 && (
                            <details>
                              <summary className="cursor-pointer px-2.5 py-1.5 text-text-secondary select-none list-none flex items-center gap-1.5">
                                <Telescope size={12} className="text-info" />
                                智能体轨迹{msg.agentRounds ? ` · ${msg.agentRounds} 轮` : ''}
                                {msg.isStreaming && <span className="text-info/80 animate-pulse">进行中…</span>}
                              </summary>
                              <div className="px-2.5 pb-2 space-y-1">
                                {msg.agentTrace!.map((t, i) => (
                                  <div key={i} className="flex items-center gap-1.5 text-text-secondary">
                                    {t.status === 'ok' ? <CheckCircle2 size={11} className="text-success shrink-0" />
                                      : t.status === 'running' ? <span className="w-[11px] h-[11px] rounded-full border border-info border-t-transparent animate-spin shrink-0" />
                                      : <XCircle size={11} className={t.status === 'rejected' ? 'text-text-muted shrink-0' : 'text-danger shrink-0'} />}
                                    <span className="truncate">第{t.round}轮 · {t.summary}</span>
                                    {t.status === 'rejected' && <span className="text-text-muted shrink-0">已跳过</span>}
                                  </div>
                                ))}
                              </div>
                            </details>
                          )}
                          {msg.pendingConfirm && (
                            <div className="mx-2.5 mb-2 p-2.5 border border-warning/50 bg-warning/5 rounded-[2px]">
                              <div className="text-text-primary mb-2">助手想执行写操作：{msg.pendingConfirm.summary}</div>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => confirmAgentAction(msg.pendingConfirm!.pending_id, true)}
                                  className="px-2.5 py-1 rounded-[2px] bg-accent text-white text-xs hover:opacity-90"
                                >确认执行</button>
                                <button
                                  onClick={() => confirmAgentAction(msg.pendingConfirm!.pending_id, false)}
                                  className="px-2.5 py-1 rounded-[2px] border border-border-color text-text-secondary text-xs hover:bg-bg-hover"
                                >跳过</button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      {/* 空知识库引导：本轮回答零引用源时，给出导入入口而不是只有泛泛拒答 */}
                      {msg.role === 'ai' && msg.noSources && !msg.isStreaming && (
                        <button
                          onClick={() => {
                            setShowChatPanel(false);
                            navigate('/ingest/batch-import');
                          }}
                          className="mt-2 flex items-center gap-1 text-xs text-info hover:underline"
                        >
                          知识库还没有相关内容，去导入 →
                        </button>
                      )}
                      {/* 规则版 FactCheck：检出无法核实的越界引用标记时给琥珀色小字提示，全部合法不显示 */}
                      {msg.role === 'ai' && !msg.isStreaming && (msg.factcheckInvalid ?? 0) > 0 && (
                        <div className="mt-1.5 text-xs text-warning">
                          本条回答含 {msg.factcheckInvalid} 个无法核实的引用标记
                        </div>
                      )}
                      {msg.isStreaming && msg.role === 'ai' && !msg.content
                        && isLocalModel(msg.model || '') && !/0\.5b|1\.5b/.test(`${msg.model} ${ollamaModel}`) && (
                        <div className="text-xs text-info/80 animate-pulse mb-1">
                          本地大模型冷加载中，首次约 10-30 秒，之后闲置 30 分钟内秒回…
                        </div>
                      )}
                      {msg.isStreaming && (
                        <span className="inline-block w-1.5 h-3 bg-info animate-pulse ml-1 rounded-sm" />
                      )}
                      <div className={`text-[10px] mt-1.5 ${msg.role === 'user' ? 'text-white/60' : 'text-text-secondary'}`}>
                        {msg.timestamp.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })}
                        {msg.model && msg.role === 'ai' && (
                          <ModelNameTag model={msg.model} />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Attachments preview */}
              <AnimatePresence>
                {attachments.length > 0 && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="flex gap-2 px-4 py-2 overflow-hidden shrink-0"
                  >
                    {attachments.map((att, i) => (
                      <div key={i} className="flex items-center gap-1.5 px-2.5 py-1 bg-bg-secondary border border-border-color rounded-[2px] text-xs text-text-primary">
                        <span>{att.name}</span>
                        <button onClick={() => setAttachments((prev) => prev.filter((_, idx) => idx !== i))} className="text-text-secondary hover:text-danger leading-none">×</button>
                      </div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>

              {renderModelRow()}
              {renderInputRow()}
            </motion.div>
          </motion.div>
          </>
        )}
      </AnimatePresence>,
      document.body
      )}

      {/* 悬浮球：右下角（吉祥物默认在 bottom-20 right-6，本球放其正下方 bottom-4 right-6）。
          **可拖动**（位置持久化，双击复位）——默认位置会压住页面右下角的内容。
          面板打开时隐藏；流式输出中显示呼吸点，有消息时显示未读角标。
          注意：点击由 useDraggableFab 的 pointerup 判定（位移 <5px 才算点击），
          故这里不能再挂 onClick，否则拖完也会触发一次打开。 */}
      {!showChatPanel && (
        <motion.button
          ref={fab.ref}
          {...fab.handlers}
          style={fab.style}
          className={`fixed fab-mobile-clear bottom-4 right-6 z-50 w-12 h-12 rounded-full glass border border-border-color text-text-secondary hover:text-text-primary shadow-lg flex items-center justify-center transition-colors ${
            fab.dragging ? 'cursor-grabbing' : 'cursor-grab'
          }`}
          title="AI 助手（可拖动 · 右键复位）"
        >
          <Bot size={22} />
          {isStreaming ? (
            <span className="absolute -top-0.5 -right-0.5 flex w-3 h-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-info opacity-60" />
              <span className="relative inline-flex rounded-full w-3 h-3 bg-info" />
            </span>
          ) : messages.length > 0 ? (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-accent text-white text-[10px] font-bold flex items-center justify-center border-2 border-bg-primary">
              {messages.length > 99 ? '99+' : messages.length}
            </span>
          ) : null}
        </motion.button>
      )}
    </>
  );
};

export default ChatInputBar;
